import UploadModal from "@/components/UploadModal";
import AppRoutes from "@/components/AppRoutes";
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import { SuccessCriteria } from "@/components/SuccessCriteria";
import { AgentBox } from "@/components/AgentBox";
import { DiagnosticsQuickActions } from "@/components/DiagnosticsQuickActions";
import { DemoBanner } from "@/components/DemoBanner";
import { useCopilot } from "@/contexts/CopilotContext";
import React, { useState, useRef, useEffect } from "react";
import { Switch, Route, Link, useLocation } from "wouter";
import AppProviders from "@/components/AppProviders";
import { PinnedFileProvider } from "@/contexts/PinnedFileContext";
import { CopilotProvider } from "@/contexts/CopilotContext";
import { DraggableProvider } from "@/contexts/DraggableContext";
import { Database, FileText, Bell, Printer, Search, Menu, ChevronLeft, Upload } from "lucide-react";

// Page imports
import Home from "@/pages/home";
import PlanPage from "@/pages/plan";
import DiagnosticPage from "@/pages/diagnostic";
import CodeReviewGuidelines from "@/pages/code-review-guidelines";
import RuleManager from "@/pages/rule-manager";
import WSRTransformationMatrixPage from "@/pages/wsr-transformation-matrix";
import { WSRSplitView } from "@/features/wsr";
import WSRPage from "@/pages/WSRPage";
import ClientLandingPage from "@/components/ClientLandingPage";
import ObservationPage from "@/pages/ObservationPage";
import NotFound from "@/pages/not-found";
import StaticAnalysisPage from "@/pages/StaticAnalysisPage";
import FileUpload from "@/components/FileUpload";
import ArchitectureAnalysisPage from "@/pages/arch-analysis";
import UserProfilePage from "@/pages/user-profile";
import BuildPage from "@/pages/build";
import TestPage from "@/pages/test";
import MonitorPage from "@/pages/monitor";
import EnvisioningPhasePage from "@/pages/envisioning-phase";
import DesignPage from "@/pages/design";
import EnterpriseConfigDemoPage from "@/pages/enterprise-demo";
import BacpacAssessmentReport from "@/pages/BacpacAssessmentReport";
import BacpacFindingsReport from "@/pages/BacpacFindingsReport";
import DiagnosticsPage from "@/pages/DiagnosticsPage";
import SQLToolPage from "@/pages/SQLToolPage";
import {
  SQLConfigurationsPage,
  SQLDatabaseDesignPage,
  SQLSecurityPage,
  SQLPerformancePage,
  SQLErrorLogsPage,
  SQLAlwaysOnPage,
  SQLMaintenancePage,
  SQLPropertiesPage,
  SQLExcellencePage
} from "@/components/SQLToolWrappers";
import SQLWorkspace from "@/components/SQLWorkspace";
import { SQLServerManagement } from "@/pages/SQLServerManagement";
import VMProfilePage from "@/pages/VMProfilePage";
import EnterpriseArchitecturePage from "@/pages/EnterpriseArchitecturePage";
import { CPUStressTestingPage } from "@/pages/CPUStressTestingPage";
import StabilizePage from "@/pages/StabilizePage";
import CostcoArchitecturePage from "@/pages/CostcoArchitecturePage";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ClientProvider } from "@/contexts/ClientContext";
import { ClientAwareLink } from "@/components/ClientAwareLink";
import { ClientAwareRedirect } from "@/components/ClientAwareRedirect";
import { useClient } from "@/contexts/ClientContext";

// SQL Tool wrapper components for routes (must be top-level)


function Router() {
  const { copilotOpen, toggleCopilot } = useCopilot();
  const [rightPaneTab, setRightPaneTab] = useState<'quick' | 'agent'>('quick');
  // Extract client code from current route
  const location = useLocation();
  // Determine if we are on the StressTestingPage route
  const isStressTestingPage = location[0]?.includes('/stabilize/stress');
  const { selectedClient, clearSelectedClient } = useClient();
  const getClientContext = () => {
    const locationPath = location[0];
    const match = /\/clients\/([^/]+)/.exec(locationPath);
    return match ? match[1].toUpperCase() : null;
  };
  const currentClient = selectedClient?.code || getClientContext();
  // State and refs for sidebar, reports dropdown, upload modal, and search
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const reportsDropdownRef = useRef<HTMLDivElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Check if we're on a public page (landing, about, or login)
  const isPublicPage = location[0] === '/' || location[0] === '/login' || location[0] === '/about' || location[0] === '/register';
  const hideGlobalLayout = isPublicPage;

  // ...existing code...
  const handleLogoClick = () => {
    clearSelectedClient();
  };

  // Enhanced context detection function for upload modal
  const getUploadContext = () => {
    const currentPath = location[0];
    if (currentPath.includes('/wsr')) {
      return {
        page: 'wsr',
        title: 'Upload Previous WSR',
        description: 'Upload last week\'s WSR file to analyze and pre-populate this week\'s report',
        expectedFileTypes: ['md', 'txt', 'pdf', 'docx'],
        analysisType: 'Weekly Status Report Analysis',
        handler: 'wsrUpload',
        instructions: [
          'Upload your previous week\'s WSR file (.md, .txt, or .pdf)',
          'The system will automatically parse and extract:',
          '• Header information (dates, contacts, project details)',
          '• Work accomplished items',
          '• Next week targets and priorities',
          '• Risks and bottlenecks',
          '• Performance metrics and KPIs',
          '• Conclusions and recommendations',
          'Parsed data will pre-populate the new WSR form'
        ]
      };
    }
    if (currentPath.includes('/arch-analysis') || currentPath.includes('/togaf') || currentPath.includes('/zachman') || currentPath.includes('/safe') || currentPath.includes('/archimate')) {
      return {
        page: 'arch-analysis',
        title: 'Upload Architecture Artifacts',
        description: 'Upload architecture diagrams, models, or documentation',
        expectedFileTypes: ['pdf', 'png', 'jpg', 'drawio', 'vsdx'],
        analysisType: 'Architecture Analysis',
        handler: 'archUpload',
        instructions: [
          'Upload architecture artifacts (PDF, PNG, JPG, Draw.io, Visio)',
          'The system will analyze and extract architecture elements',
          '• Identify layers, domains, and relationships',
          '• Map to TOGAF, Zachman, or other frameworks',
          '• Generate architecture insights and recommendations'
        ]
      };
    }
    if (currentPath.includes('/static-analysis') || currentPath.includes('/static-code-analysis')) {
      return {
        page: 'sql-static-analysis',
        title: 'Upload SQL Stored Procedure',
        description: 'Upload a .sql file containing a stored procedure for static analysis',
        expectedFileTypes: ['sql'],
        analysisType: 'SQL Stored Procedure Static Analysis',
        handler: 'sqlStaticAnalysisUpload',
        instructions: [
          'Upload a .sql file containing your stored procedure',
          'The system will analyze for:',
          '• Oversized procedures',
          '• Parameter bloating',
          '• Missing error handling',
          '• Magic values',
          '• NOLOCK usage',
          '• Cursor usage',
          '• Unsafe DELETE operations',
          '• GOTO usage',
          'and more...'
        ]
      };
    }
    // Default context
    return {
      page: 'general',
      title: 'Upload Files',
      description: 'Upload files for analysis',
      expectedFileTypes: ['pdf', 'docx', 'xlsx', 'png', 'jpg', 'json', 'xml', 'sql'],
      analysisType: 'General File Analysis',
      handler: 'generalUpload',
      instructions: [
        'Upload files for general analysis',
        'The system will attempt to:',
        '• Detect file type and content',
        '• Extract relevant information',
        '• Provide analysis recommendations'
      ]
    };
  };

  return (
    <div className="h-screen bg-white dark:bg-gray-900 transition-colors">
      {!hideGlobalLayout && (
        <Header
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          selectedClient={selectedClient}
          currentClient={currentClient ? currentClient : ""}
          handleLogoClick={handleLogoClick}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          location={location[0]}
          reportsDropdownOpen={reportsDropdownOpen}
          setReportsDropdownOpen={setReportsDropdownOpen}
          reportsDropdownRef={reportsDropdownRef}
          setUploadModalOpen={setUploadModalOpen}
        />
      )}
      <div className="flex">
        {/* Sidebar - only on authenticated pages */}
        {!hideGlobalLayout && (
          <Sidebar sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed} />
        )}
        {/* Main Content */}
        <main
          className={`flex-1 overflow-y-auto transition-all duration-300 ${sidebarCollapsed && !hideGlobalLayout ? '' : (!hideGlobalLayout ? 'ml-64' : '')} ${!hideGlobalLayout ? 'pt-[74px]' : ''} ${copilotOpen ? 'mr-[300px]' : ''}`}
        >
          {/* Copilot Toggle Button - only on authenticated pages */}
          {/* Copilot Toggle Button - persistent like IDE sidebar toggle */}
          {!hideGlobalLayout && !copilotOpen && (
            <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50">
              <button
                onClick={toggleCopilot}
                className="bg-white dark:bg-gray-800 border-l border-t border-b border-gray-200 dark:border-gray-700 w-6 h-12 rounded-l-md shadow-md flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Open Agent Pane"
              >
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          )}
          <ClientAwareRedirect>
            <AppRoutes />
          </ClientAwareRedirect>
        </main>
        {/* Right Navigation Pane (fixed right) - only on authenticated pages */}
        {copilotOpen && !isStressTestingPage && !hideGlobalLayout && (
          <aside className="w-[300px] h-screen min-h-0 max-h-none flex flex-col border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl fixed right-0 top-0 z-40">
            {/* Close handle for the pane */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full z-50">
              <button
                onClick={toggleCopilot}
                className="bg-white dark:bg-gray-800 border-l border-t border-b border-gray-200 dark:border-gray-700 w-6 h-12 rounded-l-md shadow-md flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Close Agent Pane"
              >
                <div className="rotate-180">
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </div>
              </button>
            </div>
            <div className="flex flex-row border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
              <button
                className={`flex-1 py-2 text-sm font-semibold ${rightPaneTab === 'quick' ? 'bg-white text-shiftleft-700 border-b-2 border-shiftleft-600' : 'text-gray-500 hover:text-shiftleft-700'}`}
                onClick={() => setRightPaneTab('quick')}
              >
                Quick Actions
              </button>
              <button
                className={`flex-1 py-2 text-sm font-semibold ${rightPaneTab === 'agent' ? 'bg-white text-shiftleft-700 border-b-2 border-shiftleft-600' : 'text-gray-500 hover:text-shiftleft-700'}`}
                onClick={() => setRightPaneTab('agent')}
              >
                Agent
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {rightPaneTab === 'quick' && <DiagnosticsQuickActions />}
              {rightPaneTab === 'agent' && <AgentBox />}
            </div>
          </aside>
        )}
      </div>
      {/* Upload Modal - Context Aware */}
      <UploadModal
        clientCode={currentClient ? currentClient : ""}
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        context={getUploadContext() as any}
        onUploadSuccess={(files) => {
          const context = getUploadContext() as any;
          if (context.page === 'sql-static-analysis' && files && files.length > 0) {
            // Save the last uploaded file and its analysis to localStorage
            const clientCodeKey = (currentClient ? currentClient : "").toUpperCase();
            const file = files[0];

            // Extract the analysis data from the SmartUploadHandler result
            const analysisData = {
              name: file.name,
              fileName: file.name,
              content: file.analysis?.smartAnalysis?.data?.content || '',
              observations: file.analysis?.smartAnalysis?.data?.observations || []
            };

            localStorage.setItem(`last_sql_static_analysis_${clientCodeKey}`, JSON.stringify(analysisData));
            // Route to static analysis page for this client
            if (clientCodeKey) {
              location[1](`/clients/${clientCodeKey}/static-analysis`);
            }
          }
        }}
      />
    </div>

  );
}

function App() {
  return (
    <PinnedFileProvider>
      <DraggableProvider>
        <CopilotProvider>
          <AppProviders>
            <Router />
          </AppProviders>
        </CopilotProvider>
      </DraggableProvider>
    </PinnedFileProvider>
  );
}

export default App;
